import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { MessageConversation, type ConversationSubject } from '../components/MessageConversation';
import { useAuth } from '../context/AuthContext';
import { refreshMessageSummary } from '../hooks/useMessageSummary';
import { useRefreshOnNotification } from '../hooks/useRefreshOnNotification';
import { contractApi, messageThreadApi, milestoneApi } from '../services/api';
import type { ConversationMessage, MessageThread } from '../types';
import {
  sameThread, sideOfRole, subjectIcon, subjectKey, subjectLabel, subjectPath, type ThreadSide, type ThreadType, threadActivityAt, isFinishedThread,
  formatMessageTime
} from '../utils/messageThreads';
import { apiErrorCode, messageSendErrorText } from '../utils/apiError';
import { MessageAttestationDialog } from '../components/MessageAttestationDialog';
import { MessageExportDialog } from '../components/MessageExportDialog';

/** Avslutade holds finished projects; Alla and Väntar never show them. */
type Filter = 'all' | 'unread' | 'completed';

/** One conversation: every thread with the same counterpart. */
interface Conversation {
  key: string;
  name: string;
  threads: MessageThread[];
  projects: { id: string; label: string }[];
  latest: MessageThread;
  unread: number;
}

const formatShort = (value: string, language: string) => {
  const date = new Date(value);
  const sameDay = date.toDateString() === new Date().toDateString();
  return date.toLocaleString(language === 'en' ? 'en-GB' : 'sv-SE', sameDay
    ? { hour: '2-digit', minute: '2-digit' }
    : { day: 'numeric', month: 'short' });
};
const initials = (name: string) => name.split(/[\s-]+/).filter(Boolean).map(part => part[0]).join('').slice(0, 2).toUpperCase() || '?';
const projectLabel = (thread: Pick<MessageThread, 'projectNumber' | 'projectDescription'>) =>
  `#${thread.projectNumber} ${thread.projectDescription}`;

function groupConversations(threads: MessageThread[]): Conversation[] {
  const byKey = new Map<string, MessageThread[]>();
  threads.forEach(thread => byKey.set(thread.counterpartKey, [...(byKey.get(thread.counterpartKey) ?? []), thread]));
  return Array.from(byKey, ([key, list]) => {
    const sorted = [...list].sort((a, b) => threadActivityAt(b).localeCompare(threadActivityAt(a)));
    const projects = new Map(sorted.map(thread => [thread.projectId, projectLabel(thread)]));
    return {
      key,
      name: sorted[0].counterpartName,
      threads: sorted,
      projects: Array.from(projects, ([id, label]) => ({ id, label })),
      latest: sorted[0],
      unread: sorted.reduce((sum, thread) => sum + thread.unreadCount, 0),
    };
  }).sort((a, b) => threadActivityAt(b.latest).localeCompare(threadActivityAt(a.latest)));
}

/**
 * Meddelanden (ClickUp 869fegbvk, 869feg849): one conversation per
 * counterpart, the customer for a company and the company for a customer.
 * Every stage, contract and project with that person is in it; each message
 * says what it is about and where it was written, and a reply picks its
 * subject in "Gäller". Messages stay stored on what they are about.
 */
export function Messages() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const mySide: ThreadSide = sideOfRole(user?.personaType) ?? 'person';
  const [searchParams, setSearchParams] = useSearchParams();
  const [threads, setThreads] = useState<MessageThread[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [filter, setFilter] = useState<Filter>(() => searchParams.get('view') === 'completed' ? 'completed' : 'all');
  const [subjectFilter, setSubjectFilter] = useState('all');
  // With several projects between the two parties, one project at a time
  // (ClickUp 869fjkmyw): its subjects, its messages, its export.
  const [projectFilter, setProjectFilter] = useState<string | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [messages, setMessages] = useState<ConversationMessage[] | null>(null);
  const [openedUnread, setOpenedUnread] = useState(0);
  // The subjects that had unread messages when the conversation opened: their
  // chips keep the dot and "Gäller" starts on the first, after reading.
  const [openedUnreadSubjects, setOpenedUnreadSubjects] = useState<string[]>([]);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const loadThreads = useCallback(async () => {
    try {
      const list = await messageThreadApi.list();
      setThreads(Array.isArray(list) ? list : []);
      setLoadError(false);
    } catch {
      setLoadError(true);
      setThreads(current => current ?? []);
    }
  }, []);
  useEffect(() => { void loadThreads(); }, [loadThreads]);

  // A person with both running and finished projects has a conversation in
  // each: the running one under Alla, the finished one under Avslutade.
  const archived = filter === 'completed';
  const conversations = useMemo(
    () => groupConversations((threads ?? []).filter(thread => isFinishedThread(thread) === archived)),
    [threads, archived]
  );
  const activeConversations = useMemo(
    () => groupConversations((threads ?? []).filter(thread => !isFinishedThread(thread))),
    [threads]
  );

  // `with` opens a conversation and `about` its subject. Older links name a
  // stage (`thread`) or a contract (`contract`); they open its conversation.
  const legacyType: ThreadType | null = searchParams.get('contract') ? 'contract' : searchParams.get('thread') ? 'milestone' : null;
  const legacyId = searchParams.get('contract') ?? searchParams.get('thread');
  const legacyThread = legacyType && legacyId ? threads?.find(thread => sameThread(thread, legacyType, legacyId)) : undefined;
  const openKey = searchParams.get('with') ?? legacyThread?.counterpartKey ?? null;
  const aboutParam = searchParams.get('about') ?? (legacyThread ? subjectKey(legacyThread) : null);
  const open = conversations.find(conversation => conversation.key === openKey) ?? null;
  const threadsRef = useRef(threads);
  // On a computer the inbox fills the window below its own top, so the reply
  // box and "Skicka" are always in view and only the messages scroll. The top
  // depends on the header and the page title, so it is measured.
  const inboxRef = useRef<HTMLDivElement>(null);
  // The stage opened from an attestation request (ClickUp 869fm3fy8).
  const [attestationSubject, setAttestationSubject] = useState<ConversationSubject | null>(null);
  useLayoutEffect(() => {
    const inbox = inboxRef.current;
    if (!inbox) return;
    const measure = () => {
      const scroller = inbox.closest('.main-content');
      const top = inbox.getBoundingClientRect().top + (scroller?.scrollTop ?? window.scrollY);
      inbox.style.setProperty('--messages-inbox-top', `${Math.round(top)}px`);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  threadsRef.current = threads;
  const archivedRef = useRef(archived);
  archivedRef.current = archived;

  const loadConversation = useCallback(async (key: string) => {
    const list = await messageThreadApi.conversation(key);
    setMessages(Array.isArray(list) ? list : []);
  }, []);

  useRefreshOnNotification(async () => {
    await loadThreads();
    if (openKey) await loadConversation(openKey);
  });

  // Reading is a side effect: if it fails the user is not told, the badge
  // just stays until the next try (ClickUp 869fj3q97).
  const markConversationRead = useCallback(async (key: string) => {
    try {
      await messageThreadApi.readConversation(key);
    } catch (error) {
      console.warn('Meddelanden: the conversation could not be marked as read', error);
      return;
    }
    setThreads(current => current?.map(thread =>
      thread.counterpartKey === key ? { ...thread, unreadCount: 0 } : thread) ?? current);
    void refreshMessageSummary();
  }, []);

  // Opening a conversation loads it and reads all of it; the list keeps the
  // count it had so the "Nytt" line shows where the new messages start.
  const threadsLoaded = threads !== null;
  useEffect(() => {
    if (!openKey || !threadsLoaded) { setMessages(null); return; }
    let cancelled = false;
    setMessages(null);
    setSendError(null);
    setSubjectFilter('all');
    const opened = (threadsRef.current ?? []).filter(thread => thread.counterpartKey === openKey && isFinishedThread(thread) === archivedRef.current);
    setOpenedUnread(opened.reduce((sum, thread) => sum + thread.unreadCount, 0));
    setOpenedUnreadSubjects(opened.filter(thread => thread.unreadCount > 0).map(subjectKey));
    loadConversation(openKey)
      .then(async () => {
        if (cancelled) return;
        await markConversationRead(openKey);
      })
      .catch(() => { if (!cancelled) setMessages(current => current ?? []); });
    return () => { cancelled = true; };
  }, [openKey, threadsLoaded, loadConversation, markConversationRead]);

  // A link (a notification, Att göra) opens the tab its subject is under;
  // `view=completed` is Avslutade.
  const viewParam = searchParams.get('view');
  useEffect(() => {
    if (!threads || !openKey) return;
    const matches = threads.filter(thread => thread.counterpartKey === openKey && (!aboutParam || subjectKey(thread) === aboutParam));
    const finished = viewParam === 'completed' || (matches.length > 0 && matches.every(thread => isFinishedThread(thread)));
    if (finished !== archived) setFilter(finished ? 'completed' : 'all');
    // Only when the link changes, not when the user switches tab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadsLoaded, openKey, aboutParam, viewParam]);

  const visible = conversations.filter(conversation => filter !== 'unread' || conversation.unread > 0);
  const unreadConversations = activeConversations.filter(conversation => conversation.unread > 0).length;

  const selectConversation = (key: string | null) =>
    setSearchParams(key ? { with: key, ...(archived ? { view: 'completed' } : {}) } : archived ? { view: 'completed' } : {});
  const selectFilter = (next: Filter) => {
    setFilter(next);
    // Switching between running and finished projects closes the conversation.
    if ((next === 'completed') !== archived) setSearchParams(next === 'completed' ? { view: 'completed' } : {});
  };

  // The project shown: kept while it is in the conversation; otherwise the one
  // a link names, then one with unread messages, then the latest.
  const multiProject = (open?.projects.length ?? 0) > 1;
  useEffect(() => {
    if (!open) return;
    if (projectFilter && open.projects.some(project => project.id === projectFilter)) return;
    const linked = aboutParam ? open.threads.find(thread => subjectKey(thread) === aboutParam)?.projectId
      ?? (aboutParam.startsWith('project:') ? aboutParam.slice('project:'.length) : undefined) : undefined;
    const unread = open.threads.find(thread => thread.unreadCount > 0)?.projectId;
    setProjectFilter(linked ?? unread ?? open.threads[0]?.projectId ?? open.projects[0]?.id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open?.key, open?.projects.length, aboutParam]);
  const scopedThreads = useMemo(
    () => (open ? (multiProject ? open.threads.filter(thread => thread.projectId === projectFilter) : open.threads) : []),
    [open, multiProject, projectFilter]
  );
  const scopedProjects = useMemo(
    () => (open ? (multiProject ? open.projects.filter(project => project.id === projectFilter) : open.projects) : []),
    [open, multiProject, projectFilter]
  );
  const chooseProject = (projectId: string) => {
    setProjectFilter(projectId);
    setSubjectFilter('all');
  };

  // What a reply can be about: every thread in the conversation, plus
  // "Allmänt om projektet" for each project that has no general thread yet.
  const subjects: ConversationSubject[] = useMemo(() => {
    if (!open) return [];
    // One project is shown at a time; its name is on the project row.
    const withProject = false;
    const list: ConversationSubject[] = scopedThreads.map(thread => ({
      key: subjectKey(thread),
      label: subjectLabel(thread, t),
      project: withProject ? projectLabel(thread) : '',
      icon: subjectIcon(thread.threadType),
      href: subjectPath(thread, mySide),
      unread: openedUnreadSubjects.includes(subjectKey(thread)),
      ...(thread.threadType === 'milestone' && thread.milestoneId ? {
        attestation: {
          state: thread.attestationState ?? null,
          milestoneNumber: thread.milestoneNumber,
          milestoneId: thread.milestoneId,
          contractId: thread.contractId,
          contractNumber: thread.contractNumber,
          requestPending: !!thread.attestationRequestPending,
          latestRequestAt: thread.latestRequestAt ?? null,
          nextRequestAllowedAt: thread.nextRequestAllowedAt ?? null,
        }
      } : {}),
    }));
    scopedProjects.forEach(project => {
      const key = `project:${project.id}`;
      if (list.some(subject => subject.key === key)) return;
      list.push({
        key, label: t('messageThreads.general'), project: withProject ? project.label : '', icon: subjectIcon('project'),
        href: subjectPath({ threadType: 'project', projectId: project.id, milestoneId: null, contractId: null }, mySide),
        unread: false,
      });
    });
    return list;
  }, [open, scopedThreads, scopedProjects, mySide, t, openedUnreadSubjects]);

  // The conversation endpoint returns every project with the person; this tab
  // shows the running ones (or, under Avslutade, the finished ones).
  const subjectKeys = new Set(subjects.map(subject => subject.key));
  const tabMessages = messages?.filter(message => subjectKeys.has(subjectKey(message))) ?? null;
  const shownMessages = (tabMessages ?? []).filter(message => subjectFilter === 'all' || subjectKey(message) === subjectFilter);
  const lastMessage = tabMessages?.[tabMessages.length - 1];
  const unreadSubject = subjects.find(subject => subject.unread);
  const defaultAbout = (aboutParam && subjects.some(subject => subject.key === aboutParam) ? aboutParam : '')
    || unreadSubject?.key
    || (tabMessages?.length ? subjectKey(tabMessages[tabMessages.length - 1]) : '')
    || subjects[0]?.key || '';

  const lastThread = lastMessage ? open?.threads.find(thread => sameThread(thread, lastMessage.threadType, lastMessage.threadId)) : undefined;

  // Only a failed send shows "kunde inte skickas" and keeps the draft;
  // reloading and reading afterwards are side effects (ClickUp 869fj3q97).
  // After attesting or objecting the customer stays in the conversation,
  // which reloads so the button gives way to the stage's state.
  const closeAttestation = useCallback(() => {
    setAttestationSubject(null);
    if (openKey) void Promise.all([loadConversation(openKey), loadThreads()]).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openKey]);

  const send = async (text: string, about: string, files: File[] = [], options: { requestAttestation?: boolean } = {}) => {
    if (!openKey) return;
    const [type, id] = about.split(':') as [ThreadType, string];
    setSending(true);
    setSendError(null);
    try {
      if (type === 'milestone' && options.requestAttestation) {
        // "Be om attest" (ClickUp 869fm3fy8): the customer gets the button.
        await milestoneApi.postMessage(id, text, { context: 'attestation_request', ...(files.length > 0 ? { files } : {}) });
      } else if (files.length > 0) {
        if (type === 'contract') await contractApi.postDraftMessage(id, text, files);
        else if (type === 'project') await messageThreadApi.postToProject(id, text, files);
        else await milestoneApi.postMessage(id, text, { files });
      } else if (type === 'contract') await contractApi.postDraftMessage(id, text);
      else if (type === 'project') await messageThreadApi.postToProject(id, text);
      else await milestoneApi.postMessage(id, text);
    } catch (error) {
      // A file the server refuses (type, size, content) says so (ClickUp 869fj6n1d),
      // and a reminder sent too soon says when the next can be sent (869fmfkyz).
      setSendError(messageSendErrorText(t, error, 'messageThreads.sendError', iso => formatMessageTime(iso, i18n.language)));
      setSending(false);
      // Another tab may have sent a reminder: show the stage as waiting again.
      if (apiErrorCode(error) === 'ATTESTATION_REQUEST_TOO_SOON') void loadThreads();
      throw error;
    }
    setSending(false);
    setOpenedUnread(0);
    try {
      await Promise.all([loadConversation(openKey), loadThreads()]);
    } catch (error) {
      console.warn('Meddelanden: the conversation could not be reloaded after sending', error);
    }
    await markConversationRead(openKey);
  };

  const list = (
    <section className="messages-list" aria-labelledby="messages-list-title">
      <h3 id="messages-list-title" className="visually-hidden">{t('messageThreads.people')}</h3>
      <div className="messages-filters" role="group" aria-label={t('messageThreads.filter')}>
        <button type="button" className="messages-chip" aria-pressed={filter === 'all'} onClick={() => selectFilter('all')}>
          {t('messageThreads.all')}
        </button>
        <button type="button" className="messages-chip" aria-pressed={filter === 'unread'} onClick={() => selectFilter('unread')} aria-label={t('messageThreads.unreadFilterLabel', { count: unreadConversations })} data-testid="messages-filter-unread">
          {t('messageThreads.unreadFilter', { count: unreadConversations })}
        </button>
        <button type="button" className="messages-chip" aria-pressed={archived} onClick={() => selectFilter('completed')} data-testid="messages-filter-completed">
          {t('messageThreads.completedFilter')}
        </button>
      </div>
      {loadError && <p className="alert alert-warning m-3 mb-0" role="alert">{t('messageThreads.loadError')}</p>}
      {visible.length === 0 ? (
        <p className="messages-empty" data-testid="messages-empty">
          {t(filter === 'unread' ? 'messageThreads.noneUnread' : archived ? 'messageThreads.noneCompleted' : 'messageThreads.none')}
        </p>
      ) : (
        <ul className="messages-threads">
          {visible.map(conversation => {
            const last = conversation.latest;
            const lastMessage = last.lastMessage;
            const mine = lastMessage?.side === mySide;
            return (
              <li key={conversation.key}>
                <button
                  type="button"
                  className={`messages-thread messages-person${conversation.unread ? ' is-unread' : ''}`}
                  aria-current={conversation.key === openKey ? 'true' : undefined}
                  onClick={() => selectConversation(conversation.key)}
                  data-testid="messages-thread"
                >
                  <span className="messages-avatar" aria-hidden="true">{initials(conversation.name)}</span>
                  <span className="messages-thread-title">
                    <span className="messages-thread-project">{conversation.name}</span>
                    <span className="messages-thread-stage">
                      <i className={`feather-${subjectIcon(last.threadType)}`} aria-hidden="true"></i>{' '}{subjectLabel(last, t)}
                    </span>
                  </span>
                  <time className="messages-thread-time" dateTime={threadActivityAt(last)}>
                    {threadActivityAt(last) ? formatShort(threadActivityAt(last), i18n.language) : ''}
                  </time>
                  <span className="messages-thread-preview">
                    {lastMessage ? <>
                      <span className="messages-thread-origin">{t(`messageThreads.origin.${lastMessage.origin ?? 'message'}`)} ·</span>{' '}
                      {mine ? `${t('messageThreads.you')}: ` : ''}{lastMessage.message}
                    </> : <span className="messages-thread-origin">{t('messageThreads.noMessagesYet')}</span>}
                  </span>
                  <span className="messages-thread-projects">
                    {conversation.projects.map(project => project.label).join(', ')} · {t('messageThreads.subjectCount', { count: conversation.threads.length })}
                  </span>
                  {conversation.unread > 0 && (
                    <span className="messages-thread-tags">
                      <span className="messages-pill is-new">{t('messageThreads.unread', { count: conversation.unread })}</span>
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );

  const conversationView = open ? (
    <section className="messages-thread-view" aria-labelledby="messages-thread-title">
      <header className="messages-thread-head">
        <button type="button" className="messages-back" onClick={() => selectConversation(null)} data-testid="messages-back">
          <i className="feather-chevron-left" aria-hidden="true"></i>{t('messageThreads.back')}
        </button>
        <div className="messages-thread-head-text">
          <h3 id="messages-thread-title">{open.name}</h3>
          <p>{open.projects.map(project => project.label).join(', ')}</p>
        </div>
        {scopedProjects.length === 1 && (
          <button type="button" className="messages-export" onClick={() => setExportOpen(true)} data-testid="messages-export">
            <i className="feather-download" aria-hidden="true"></i>
            <span>{t('messageExport.button')}</span>
          </button>
        )}
      </header>
      {exportOpen && scopedProjects[0] && (
        <MessageExportDialog
          projectId={scopedProjects[0].id}
          projectLabel={scopedProjects[0].label}
          subjects={subjects}
          chosenSubject={subjectFilter}
          onClose={() => setExportOpen(false)}
        />
      )}
      {multiProject && (
        <div className="messages-projects" role="group" aria-label={t('messageThreads.projectFilter')} data-testid="messages-projects">
          <span className="messages-projects-label">{t('messageThreads.project')}</span>
          {open.projects.map(project => {
            const unread = open.threads.some(thread => thread.projectId === project.id && thread.unreadCount > 0);
            return (
              <button key={project.id} type="button" className="messages-chip messages-project-chip" aria-pressed={projectFilter === project.id}
                onClick={() => chooseProject(project.id)} data-testid="messages-project-chip">
                {project.label}
                {unread && <span className="messages-chip-dot" role="img" aria-label={t('messageThreads.state.unread')}></span>}
              </button>
            );
          })}
        </div>
      )}
      {scopedThreads.length > 1 && (
        <div className="messages-subjects" role="group" aria-label={t('messageThreads.subjectFilter')}>
          <button type="button" className="messages-chip" aria-pressed={subjectFilter === 'all'} onClick={() => setSubjectFilter('all')}>
            {t('messageThreads.allSubjects')}
          </button>
          {scopedThreads.map(thread => (
            <button
              key={subjectKey(thread)}
              type="button"
              className="messages-chip"
              aria-pressed={subjectFilter === subjectKey(thread)}
              onClick={() => setSubjectFilter(subjectKey(thread))}
              data-testid="messages-subject-chip"
            >
              {subjectLabel(thread, t)}
              {openedUnreadSubjects.includes(subjectKey(thread)) && <span className="messages-chip-dot" role="img" aria-label={t('messageThreads.state.unread')}></span>}
            </button>
          ))}
        </div>
      )}
      {messages === null ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status"><span className="visually-hidden">{t('common.loading')}</span></div>
        </div>
      ) : (
        <MessageConversation
          messages={shownMessages}
          subjects={subjects}
          mySide={mySide}
          counterpartName={open.name}
          defaultAbout={subjectFilter !== 'all' ? subjectFilter : defaultAbout}
          unreadCount={subjectFilter === 'all' ? openedUnread : 0}
          counterpartReadAt={lastThread?.counterpartReadAt ?? null}
          sending={sending}
          error={sendError}
          onSend={send}
          onOpenAttestation={mySide === 'person' ? setAttestationSubject : undefined}
          hideSubjectLines={subjectFilter !== 'all'}
        />
      )}
    </section>
  ) : (
    <section className="messages-thread-view is-empty" aria-label={t('messageThreads.conversation')}>
      <p className="messages-empty">{t('messageThreads.choose')}</p>
    </section>
  );

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('messageThreads.title')}</h5>
          </div>
          <Breadcrumb items={[{ label: t('nav.home'), path: '/home' }, { label: t('messageThreads.title') }]} />
        </div>
        <div className="page-header-right ms-auto d-none d-lg-block">
          <p className="fw-bold text-muted mb-0">{t(mySide === 'company' ? 'messageThreads.subtitleCompany' : 'messageThreads.subtitlePerson')}</p>
        </div>
      </div>
      {threads === null ? (
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status"><span className="visually-hidden">{t('common.loading')}</span></div>
        </div>
      ) : (
        <div ref={inboxRef} className={`card messages-inbox${open ? ' is-open' : ''}`} data-testid="messages-inbox">
          {list}
          {conversationView}
        </div>
      )}
      {attestationSubject?.attestation?.contractId && (
        <MessageAttestationDialog
          milestoneId={attestationSubject.attestation.milestoneId}
          contractId={attestationSubject.attestation.contractId}
          contractNumber={attestationSubject.attestation.contractNumber ?? undefined}
          onClose={closeAttestation}
        />
      )}
    </DashboardLayout>
  );
}
